import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Typography, notification } from "antd";
import { pdf } from "@react-pdf/renderer";
import imageCompression from "browser-image-compression";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-regular-svg-icons";
import { faCheckCircle, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { orderHistory } from "../../../controllers/V1/promocodes/promocodeController";
import { getPublicContent } from "../../../controllers/V1/raffleCoupon/raffleCouponContentController";
import { DEFAULT_COUPON_CONTENT, mergeCouponContent } from "../raffleCoupon/couponContentDefaults";
import { resolveImageUrl } from "../raffleCoupon/BuyWinCouponCard";
import Loading from "../../../components/Loading/LoadingEffect";
import OrderLeftStubPdf from "./orderLeftStubPdf";

const BATCH_LIMIT = 5000;

const EXPORT_SELECT = {
  order_no: true,
  buyer_first_name: true,
  buyer_last_name: true,
  buyer_mobile: true,
  buyer_user_id: true,
  cpr_number: true,
  coupon_code: true,
  draw_date: true,
  draw_time: true,
};

const loadLogoForPdf = async (url) => {
  if (!url) return "";
  try {
    const response = await fetch(url);
    if (!response.ok) return url;
    const blob = await response.blob();
    const file = new File([blob], "logo", { type: blob.type || "image/png" });
    const compressed = await imageCompression(file, {
      maxWidthOrHeight: 200,
      maxSizeMB: 0.05,
      initialQuality: 0.85,
      useWebWorker: true,
      fileType: "image/jpeg",
    });
    return await imageCompression.getDataUrlFromFile(compressed);
  } catch {
    return url;
  }
};

const fetchAllOrders = async (condition, onProgress) => {
  const option = {
    isExport: true,
    sort: { createdAt: -1 },
    condition,
    select: EXPORT_SELECT,
    skip: 0,
    limit: BATCH_LIMIT,
  };

  const allResults = [];
  let hasMore = true;

  while (hasMore) {
    const res = await orderHistory(option);
    if (res?.status === true && res?.result?.length > 0) {
      allResults.push(...res.result);
      option.skip += option.limit;
      hasMore = res.result.length >= option.limit;
      onProgress?.(`Fetching orders... (${allResults.length})`);
    } else {
      hasMore = false;
    }
  }

  return allResults;
};

const buildStubsFromOrders = (orders = []) => {
  const stubs = [];

  for (let i = 0; i < orders.length; i++) {
    const order = orders[i];
    const name = [order.buyer_first_name, order.buyer_last_name].filter(Boolean).join(" ");
    const cpr = order.cpr_number || "N/A";
    const mob = order.buyer_user_id?.country_code
      ? `${order.buyer_user_id.country_code} ${order.buyer_mobile || ""}`.trim()
      : String(order.buyer_mobile || "");
    const drawDateFormatted =
      order?.draw_date && order.draw_date !== "N/A"
        ? moment(`${order.draw_date} ${order.draw_time || "00:00"}:00`).format("DD-MM-YYYY hh:mm A")
        : "N/A";
    const orderNo = order.order_no || "N/A";

    if (!order.coupon_code) {
      stubs.push({
        order_no: orderNo,
        name,
        cpr,
        mob,
        raffleCoupon: "",
        drawDateFormatted,
      });
      continue;
    }

    const couponCodes = order.coupon_code.split(",");
    for (let j = 0; j < couponCodes.length; j++) {
      const raffleCoupon = couponCodes[j].trim();
      if (!raffleCoupon) continue;
      stubs.push({
        order_no: orderNo,
        name,
        cpr,
        mob,
        raffleCoupon,
        drawDateFormatted,
      });
    }
  }

  return stubs;
};

const DownloadOrderStubsPopup = ({ popUpOpen, togglePopUp }) => {
  const handleCancel = () => togglePopUp();

  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Preparing stub PDF...");
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(moment().startOf("day").format("YYYY-MM-DDTHH:mm"));
  const [toDate, setToDate] = useState(moment().endOf("day").format("YYYY-MM-DDTHH:mm"));

  const handleDownload = async (e) => {
    e.preventDefault();
    setError(null);

    if (!fromDate || !toDate) {
      setError("Please select both start and end date & time.");
      return;
    }

    if (moment(fromDate).isAfter(moment(toDate))) {
      setError("Start date & time cannot be after end date & time.");
      return;
    }

    try {
      setIsLoading(true);
      setLoadingText("Fetching orders...");

      const from = moment(fromDate).format("YYYY-MM-DD HH:mm:ss");
      const to = moment(toDate).format("YYYY-MM-DD HH:mm:ss");
      const baseCondition = {
        createdAt: { $gte: from, $lte: to },
        status: "Active",
      };

      const contentPromise = getPublicContent();

      const allOrders = await fetchAllOrders(baseCondition, setLoadingText);

      if (!allOrders.length) {
        setError("No orders found for the selected date range.");
        setIsLoading(false);
        return;
      }

      const contentRes = await contentPromise;
      const couponContent = mergeCouponContent(
        contentRes.status === true && contentRes.result
          ? contentRes.result
          : DEFAULT_COUPON_CONTENT
      );

      const logoPromise = loadLogoForPdf(resolveImageUrl(couponContent.logo_image));
      const stubs = buildStubsFromOrders(allOrders);
      if (!stubs.length) {
        setError("No raffle coupon stubs found for the selected orders.");
        setIsLoading(false);
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 0));

      setLoadingText(`Generating PDF (${stubs.length} stub(s))...`);
      const logoSrc = await logoPromise;

      const blob = await pdf(
        <OrderLeftStubPdf stubs={stubs} logoSrc={logoSrc} content={couponContent} />
      ).toBlob();

      const fileName = `Order_Left_Stubs_${moment(fromDate).format("DD-MM-YYYY_HHmm")}_to_${moment(toDate).format("DD-MM-YYYY_HHmm")}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      notification.open({
        message: "Success",
        description: `Downloaded ${stubs.length} left tear-off stub(s).`,
        placement: "topRight",
        icon: <FontAwesomeIcon icon={faCheckCircle} style={{ color: "green" }} />,
        duration: 3,
      });

      handleCancel();
    } catch (err) {
      console.error("Stub PDF download failed:", err);
      setError("An error occurred while generating the PDF.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={popUpOpen}
      onClose={handleCancel}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        className: "myDialogPopUp",
      }}
    >
      <div className="myDrawer">
        <div className="inventory_popup_title">
          <Typography
            variant="h5"
            component="div"
            gutterBottom
            className="inventory_popup_title_heading"
          >
            Download Left Tear-off Stubs
          </Typography>
          <Link
            to={false}
            style={{ position: "absolute", right: 8, top: 8, color: "#dd5151" }}
            onClick={handleCancel}
          >
            <FontAwesomeIcon icon={faTimesCircle} />
          </Link>
        </div>

        <Typography variant="body1" component="div" className="my_delete_popup_paragraph">
          <div className="container-fluid">
            <div className="categories_open_popup_main_contnet">
              <form onSubmit={handleDownload}>
                <div className="form-group row">
                  <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                    <p style={{ marginBottom: 16, color: "#666" }}>
                      Select a date & time range to download left tear-off stubs (9 per A4 page) for active orders.
                    </p>
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label htmlFor="stub_from_date" className="all_lable_for_vendor_dashbaord">
                      Start Date & Time :
                    </label>
                    <input
                      type="datetime-local"
                      name="from_date"
                      id="stub_from_date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="form-control categories_select_tg_enter_product"
                      required
                    />
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                    <label htmlFor="stub_to_date" className="all_lable_for_vendor_dashbaord">
                      End Date & Time :
                    </label>
                    <input
                      type="datetime-local"
                      name="to_date"
                      id="stub_to_date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="form-control categories_select_tg_enter_product"
                      required
                    />
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                    <div className="inventory_open_popup_action_btn">
                      <button
                        type="button"
                        className="btn btn-light categories_cancel_button"
                        onClick={handleCancel}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-danger" disabled={isLoading}>
                        Download PDF
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </Typography>

        {error && (
          <div style={{ color: "red", padding: "0 16px 12px", display: "flex", alignItems: "center", gap: 8 }}>
            <FontAwesomeIcon icon={faExclamationCircle} />
            {error}
          </div>
        )}
      </div>
      <Loading isLoading={isLoading} text={loadingText} />
    </Dialog>
  );
};

export default DownloadOrderStubsPopup;
